import { useEffect, useState, type RefObject } from 'react';

export type ContainerTier = 'mobile' | 'desktop';

const MOBILE_MAX = 720;

/**
 * Watches the inline-size of an element and reports whether it's a
 * "mobile-shaped" container. Pair with `container-type: inline-size` /
 * `@container` CSS so the JS tier and the CSS tier agree on what
 * "mobile" means (≤ 720px).
 *
 * The hook starts with a SSR-safe `'desktop'` until the first
 * ResizeObserver tick — components branching on this should expect
 * one extra render after mount on phones.
 */
export function useContainerSize(ref: RefObject<HTMLElement | null>): ContainerTier {
  const [tier, setTier] = useState<ContainerTier>('desktop');

  useEffect(() => {
    const el = ref.current;
    if (el === null) return undefined;

    // Seed once synchronously to avoid a desktop-then-mobile flash on
    // mount when the calendar is embedded in a narrow shell.
    const initial = el.getBoundingClientRect().width;
    setTier(initial <= MOBILE_MAX ? 'mobile' : 'desktop');

    const obs = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setTier(w <= MOBILE_MAX ? 'mobile' : 'desktop');
    });
    obs.observe(el);
    return () => { obs.disconnect(); };
  }, [ref]);

  return tier;
}
